import { lazy, Suspense, type ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AuthShell, Shell } from '@/components/Shell';
import { AuthGuard } from '@/components/AuthGuard';
import { PlatformRoute } from '@/components/PlatformRoute';
import { CertificadorRoute } from '@/components/CertificadorRoute';
import { PortalGuard } from '@/components/Portal/PortalGuard';
import {
  AdminClientesPage,
  AdminTrilhaPage,
  AdminUsuariosPage,
  AnalisePage,
  ApiKeysPage,
  CasoDestinoPage,
  CasosPage,
  CertificacaoPage,
  ConfigPage,
  DossiePage,
  EscritorioPage,
  ForbiddenPage,
  HomePage,
  LaudoCasoPage,
  MesaEletronicaPage,
  LoginPage,
  MembersPage,
  NotFoundPage,
  NovoCasoPage,
  OnboardingPage,
  PerfilPage,
  PreAnalisePage,
  PrecificacaoPage,
  ResetPasswordPage,
  ServerErrorPage,
  SignupPage,
  TrocarSenhaPage,
  WorkspaceCasoPage,
} from '@/pages';

// CaseWizardPage é carregada lazy porque o bundle de Fabric.js (E8.F4)
// entra pela mesma página — mantemos o code-splitting.
const CaseWizardPage = lazy(async () => {
  const mod = await import('./pages/CaseWizardPage');
  return { default: mod.CaseWizardPage };
});

// Portal do Cliente (E50.F2): chunk lazy próprio — shell, tema e páginas do
// portal só baixam quando um usuário de portal navega para /portal/*.
const PortalRoutes = lazy(async () => {
  const mod = await import('./pages/portal/PortalRoutes');
  return { default: mod.PortalRoutes };
});

export interface AppRoutesProps {
  version?: string;
}

function ShellRoute({
  children,
  fullBleed,
  guarded = true,
}: {
  children: ReactNode;
  fullBleed?: boolean;
  guarded?: boolean;
}) {
  const inner = <Shell {...(fullBleed ? { fullBleed: true } : {})}>{children}</Shell>;
  // E50.F2: PortalGuard area="perito" devolve usuários de portal (ambiente
  // escritorio/banco) para /portal/casos. Fail-open: loading/erro/interno
  // renderizam como sempre — zero mudança para o legado.
  return guarded ? (
    <AuthGuard>
      <PortalGuard area="perito">{inner}</PortalGuard>
    </AuthGuard>
  ) : (
    inner
  );
}

export function AppRoutes(_props: AppRoutesProps = {}) {
  void _props;
  return (
    <Routes>
      {/* Pré-auth: layout centralizado, sem chrome */}
      <Route
        path="/login"
        element={
          <AuthShell>
            <LoginPage />
          </AuthShell>
        }
      />
      <Route
        path="/signup"
        element={
          <AuthShell>
            <SignupPage />
          </AuthShell>
        }
      />
      <Route
        path="/reset-password"
        element={
          <AuthShell>
            <ResetPasswordPage />
          </AuthShell>
        }
      />
      {/* E65.F3 (ADR-0069): troca obrigatória da senha provisória. Fora do
          ShellRoute de propósito — sem menu, sem saída lateral: quem chega aqui
          não alcança nenhuma outra superfície até trocar, e o PortalGuard, que
          é quem traz a pessoa para cá, não pode envolver a própria tela sob pena
          de laço. */}
      <Route
        path="/trocar-senha"
        element={
          <AuthGuard>
            <AuthShell bare>
              <TrocarSenhaPage />
            </AuthShell>
          </AuthGuard>
        }
      />
      <Route
        path="/onboarding"
        element={
          <AuthGuard>
            <AuthShell bare>
              <OnboardingPage />
            </AuthShell>
          </AuthGuard>
        }
      />

      {/* Home pública (com chrome se logado, mas sem AuthGuard) */}
      <Route
        path="/"
        element={
          <ShellRoute guarded={false}>
            <HomePage />
          </ShellRoute>
        }
      />

      {/* Rotas protegidas (Shell completo + AuthGuard) */}
      <Route
        path="/casos"
        element={
          <ShellRoute>
            <CasosPage />
          </ShellRoute>
        }
      />
      <Route
        path="/casos/novo"
        element={
          <ShellRoute>
            <NovoCasoPage />
          </ShellRoute>
        }
      />
      {/* E78.F1 — resolvedor de destino: carrega o caso e manda para a tela em
          que o trabalho está. Era um placeholder, e a esteira "Criação"
          apontava para ele. */}
      <Route
        path="/casos/:id"
        element={
          <ShellRoute>
            <CasoDestinoPage />
          </ShellRoute>
        }
      />
      <Route
        path="/casos/:id/wizard"
        element={
          <ShellRoute>
            <Suspense fallback={<div data-testid="case-wizard-loading" />}>
              <CaseWizardPage />
            </Suspense>
          </ShellRoute>
        }
      />
      <Route
        path="/casos/:id/pre-analise"
        element={
          <ShellRoute>
            <PreAnalisePage />
          </ShellRoute>
        }
      />
      <Route
        path="/casos/:id/workspace"
        element={
          <ShellRoute fullBleed>
            <WorkspaceCasoPage />
          </ShellRoute>
        }
      />
      <Route
        path="/casos/:id/mesa-eletronica"
        element={
          <ShellRoute>
            <MesaEletronicaPage />
          </ShellRoute>
        }
      />
      <Route
        path="/casos/:id/laudo"
        element={
          <ShellRoute>
            <LaudoCasoPage />
          </ShellRoute>
        }
      />
      <Route
        path="/analise/:id"
        element={
          <ShellRoute>
            <AnalisePage />
          </ShellRoute>
        }
      />
      <Route
        path="/dossie/:id"
        element={
          <ShellRoute>
            <DossiePage />
          </ShellRoute>
        }
      />
      <Route
        path="/config"
        element={
          <ShellRoute>
            <ConfigPage />
          </ShellRoute>
        }
      />
      <Route
        path="/perfil"
        element={
          <ShellRoute>
            <PerfilPage />
          </ShellRoute>
        }
      />
      <Route
        path="/configuracoes/api-keys"
        element={
          <ShellRoute>
            <ApiKeysPage />
          </ShellRoute>
        }
      />
      <Route
        path="/configuracoes/usuarios"
        element={
          <ShellRoute>
            <MembersPage />
          </ShellRoute>
        }
      />
      <Route
        path="/configuracoes/escritorio"
        element={
          <ShellRoute>
            <EscritorioPage />
          </ShellRoute>
        }
      />
      {/* Certificação (E52.F3, T3): superfície interna da plataforma, gate
          COMPOSTO — escritório-plataforma + papel certificador. */}
      <Route
        path="/certificacao"
        element={
          <ShellRoute>
            <CertificadorRoute>
              <CertificacaoPage />
            </CertificadorRoute>
          </ShellRoute>
        }
      />
      {/* Console de administração (E53.F5, ADR-0064 §4): área própria, com a
          navegação e as rotas condicionadas ao gate de plataforma. */}
      <Route
        path="/admin/clientes"
        element={
          <ShellRoute>
            <PlatformRoute>
              <AdminClientesPage />
            </PlatformRoute>
          </ShellRoute>
        }
      />
      <Route
        path="/admin/usuarios"
        element={
          <ShellRoute>
            <PlatformRoute>
              <AdminUsuariosPage />
            </PlatformRoute>
          </ShellRoute>
        }
      />
      <Route
        path="/admin/trilha"
        element={
          <ShellRoute>
            <PlatformRoute>
              <AdminTrilhaPage />
            </PlatformRoute>
          </ShellRoute>
        }
      />
      <Route
        path="/configuracoes/precificacao"
        element={
          <ShellRoute>
            <PlatformRoute>
              <PrecificacaoPage />
            </PlatformRoute>
          </ShellRoute>
        }
      />
      {/* Portal do Cliente (E50.F2) — chunk lazy; guards vivem dentro de
          PortalRoutes (AuthGuard > PortalGuard > PortalShell). */}
      <Route
        path="/portal/*"
        element={
          <Suspense fallback={<div data-testid="portal-loading" />}>
            <PortalRoutes />
          </Suspense>
        }
      />

      {/* Páginas de erro navegacionais (E13.F2.T08) — úteis para deep-link
          e para o smoke E2E exercitar Forbidden/ServerError sem precisar
          provocar um throw real. */}
      <Route
        path="/403"
        element={
          <ShellRoute guarded={false}>
            <ForbiddenPage />
          </ShellRoute>
        }
      />
      <Route
        path="/500"
        element={
          <ShellRoute guarded={false}>
            <ServerErrorPage />
          </ShellRoute>
        }
      />
      <Route
        path="*"
        element={
          <ShellRoute guarded={false}>
            <NotFoundPage />
          </ShellRoute>
        }
      />
    </Routes>
  );
}
